import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getDefaultRedirectPath } from '../utils/rbac';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (profileError) {
        console.error('Error fetching profile:', profileError?.message);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error in fetchUserProfile:', err?.message);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError?.message);
          // If refresh token is invalid, clear the session
          if (
            sessionError?.message?.includes('Refresh Token Not Found') ||
            sessionError?.message?.includes('Invalid Refresh Token') ||
            sessionError?.name === 'AuthApiError'
          ) {
            await supabase?.auth?.signOut();
            setUser(null);
            setProfile(null);
          }
          setLoading(false);
          return;
        }

        if (session?.user) {
          setUser(session?.user);
          const userProfile = await fetchUserProfile(session?.user?.id);
          setProfile(userProfile);
        }
      } catch (err) {
        console.error('Auth initialization error:', err?.message);
        // Handle invalid refresh token during initialization
        if (
          err?.message?.includes('Refresh Token Not Found') ||
          err?.message?.includes('Invalid Refresh Token') ||
          err?.name === 'AuthApiError'
        ) {
          try { await supabase?.auth?.signOut(); } catch (_) {}
          setUser(null);
          setProfile(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (event === 'TOKEN_REFRESHED' && !session) {
          // Token refresh failed — clear state
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        if (session?.user) {
          setUser(session?.user);
          const userProfile = await fetchUserProfile(session?.user?.id);
          setProfile(userProfile);
        } else {
          setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Check for invalid API key error
        if (error?.message?.includes('Invalid API key') || 
            error?.message?.includes('API key')) {
          throw new Error('Database configuration error. Please contact system administrator.');
        }
        throw error;
      }

      if (data?.user) {
        const userProfile = await fetchUserProfile(data?.user?.id);
        setUser(data?.user);
        setProfile(userProfile);
        return { user: data?.user, profile: userProfile };
      }

      return data;
    } catch (err) {
      console.error('Sign in error:', err?.message);
      throw err;
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}) => {
    try {
      setError(null);

      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location?.origin}/email-verification-confirmation`
        }
      });

      if (signUpError) {
        setError(signUpError?.message);
        throw signUpError;
      }

      return data;
    } catch (err) {
      console.error('Sign up error:', err?.message);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      const { error: signOutError } = await supabase?.auth?.signOut();
      
      if (signOutError) {
        setError(signOutError?.message);
        throw signOutError;
      }

      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Sign out error:', err?.message);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email);
      
      if (resetError) {
        setError(resetError?.message);
        throw resetError;
      }
    } catch (err) {
      console.error('Reset password error:', err?.message);
      throw err;
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      setError(null);
      if (!user) throw new Error('No user logged in');

      const { data, error: updateError } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (updateError) {
        setError(updateError?.message);
        throw updateError;
      }

      setProfile(data);
      return data;
    } catch (err) {
      console.error('Update profile error:', err?.message);
      throw err;
    }
  };

  // Get redirect path based on role
  const getRedirectPath = (profileOverride) => {
    const activeProfile = profileOverride || profile;
    if (!activeProfile?.role) return '/dashboard';
    return getDefaultRedirectPath(activeProfile?.role);
  };

  const value = {
    user,
    profile,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    getRedirectPath,
    isAuthenticated: !!user,
    role: profile?.role || null,
    status: profile?.status || null
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
